import { NavLink } from 'react-router-dom';
import { LayoutGrid, Table2, Activity, Building2, ExternalLink, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BrandStat } from '@/lib/api';

interface NavEntry {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  end?: boolean;
}

const NAV: NavEntry[] = [
  { to: '/', label: 'Product Map', icon: LayoutGrid, end: true },
  { to: '/table', label: 'Price List', icon: Table2 },
  { to: '/admin/import', label: 'Import CSV', icon: Upload },
];

export interface AppSidebarProps {
  brands?: BrandStat[];
  className?: string;
}

export function AppSidebar({ brands = [], className }: AppSidebarProps) {
  return (
    <aside
      className={cn(
        'flex w-60 shrink-0 flex-col gap-6 border-r bg-card px-4 py-6',
        className
      )}
    >
      <div className="px-2">
        <div className="text-base font-bold tracking-tight">
          <span className="text-primary">GearVN</span>
          <span className="text-muted-foreground"> · Monitor</span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">Internal dashboard</div>
      </div>

      <nav className="flex flex-col gap-0.5">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-col gap-2">
        <div className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Tools
        </div>
        <a
          href="/test"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <Activity className="h-4 w-4" />
          API Smoke Test
          <ExternalLink className="ml-auto h-3 w-3" />
        </a>
      </div>

      {brands.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Báo cáo hãng
          </div>
          <nav className="flex flex-col gap-0.5">
            {brands.slice(0, 8).map((b) => (
              <NavLink
                key={b.brand}
                to={`/report/${b.brand.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    'flex items-center justify-between rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )
                }
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5" />
                  {b.brand}
                </span>
                <span className="text-xs tabular-nums">{b.count}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      <div className="mt-auto px-3 text-[11px] leading-tight text-muted-foreground">
        v0.1.0 · D1 + Pages
      </div>
    </aside>
  );
}
